import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Diamond as DiamondIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from "@mui/icons-material";
import Navbar from "../../components/navbar/navbar.component";
import { schemesV2Api } from "../../services/schemesV2Api";
import { Image } from "react-bootstrap";
import HighlyRecommended from "../../assets/HighlyRecommended.png";
const SchemesV2Selection = () => {
  const navigate = useNavigate();
  const [schemes, setSchemes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    const fetchSchemes = async () => {
      try {
        setIsLoading(true);
        setError(null);

        let schemesData = [];

        try {
          // Try to use the API first
          const schemesResponse = await schemesV2Api.getAllSchemes();
          const rawSchemes =
            schemesResponse.data.schemes ||
            schemesResponse.data.data?.schemes ||
            [];

          // Process the schemes to convert string values to numbers
          schemesData = rawSchemes.map((scheme) => ({
            ...scheme,
            monthlyInstalment: scheme.monthlyInstalment
              ? Number(scheme.monthlyInstalment)
              : null,
            dailyInstalment: scheme.dailyInstalment
              ? Number(scheme.dailyInstalment)
              : null,
            totalAmount: Number(scheme.totalAmount) || 0,
            minAmount: Number(scheme.minAmount) || 1000,
            maxAmount: Number(scheme.maxAmount) || 50000,
            gracePeriodsAfterDue: Number(scheme.gracePeriodsAfterDue) || 5,
          }));
        } catch (apiError) {
          console.warn("API failed, using local data:", apiError.message);
          // Fallback to local data if API fails
          const response = await fetch("/src/data/schemesV2Data.json");
          const data = await response.json();
          schemesData = data.schemes || [];
        }

        setSchemes(schemesData);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSchemes();
  }, []);

  const getSchemeIcon = (schemeId) => {
    switch (schemeId) {
      case "11-month-direct-purchase":
        return <DiamondIcon sx={{ fontSize: 40, color: "#A36E29" }} />;
      case "30-day-daily-savings":
        return <CalendarIcon sx={{ fontSize: 40, color: "#A36E29" }} />;
      case "10-month-accumulation":
        return <TrendingUpIcon sx={{ fontSize: 40, color: "#A36E29" }} />;
      default:
        return <MoneyIcon sx={{ fontSize: 40, color: "#A36E29" }} />;
    }
  };

  const getSchemeDescription = (schemeId) => {
    switch (schemeId) {
      case "11-month-direct-purchase":
        return "Save monthly for 11 months and get 100% making charge waiver on gold & diamond jewelry";
      case "30-day-daily-savings":
        return "Save daily for 30 days and receive a store bonus equal to one day's installment";
      case "10-month-accumulation":
        return "Save monthly for 10 months and get 50% bonus on your one installment's amount";
      default:
        return "A flexible savings plan for your jewelry purchases";
    }
  };

  const getSchemeBenefits = (schemeId) => {
    switch (schemeId) {
      case "11-month-direct-purchase":
        return [
          "100% Making Charge Waiver",
          "Gold & Diamond Jewelry",
          "11 Month Duration",
        ];
      case "30-day-daily-savings":
        return ["Store Bonus Reward", "Daily Payments", "30 Day Duration"];
      case "10-month-accumulation":
        return [
          "50% of one installment's amount",
          "Monthly Payments",
          "10 Month Duration",
        ];
      default:
        return ["Flexible Savings", "Jewelry Purchase", "Custom Duration"];
    }
  };

  const getDummyStats = (schemeId) => {
    switch (schemeId) {
      case "11-month-direct-purchase":
        return {
          enrolled: 320,
          invested: "2.8Cr",
          avgMonthly: "₹25,000",
          completionRate: "94%",
          satisfaction: "4.8/5",
        };
      case "30-day-daily-savings":
        return {
          enrolled: 180,
          invested: "1.2Cr",
          avgDaily: "₹1,200",
          completionRate: "89%",
          satisfaction: "4.6/5",
        };
      case "10-month-accumulation":
        return {
          enrolled: 250,
          invested: "2.0Cr",
          avgMonthly: "₹20,000",
          completionRate: "92%",
          satisfaction: "4.7/5",
        };
      default:
        return {
          enrolled: 150,
          invested: "1.5Cr",
          avgMonthly: "₹15,000",
          completionRate: "88%",
          satisfaction: "4.5/5",
        };
    }
  };

  const getDummyReviews = (schemeId) => {
    const allReviews = [
      {
        name: "Priya Sharma",
        rating: 5,
        comment:
          "Excellent scheme! Got my gold necklace with zero making charges.",
      },
      {
        name: "Rajesh Kumar",
        rating: 5,
        comment: "Very convenient monthly payments. Highly recommended!",
      },
      {
        name: "Sunita Patel",
        rating: 4,
        comment:
          "Great way to save for jewelry. The bonus amount is a nice surprise.",
      },
      {
        name: "Amit Singh",
        rating: 5,
        comment:
          "Perfect for planning big purchases. Customer service is excellent.",
      },
      {
        name: "Kavita Reddy",
        rating: 4,
        comment: "Flexible and reliable. My family loves this scheme.",
      },
      {
        name: "Deepak Verma",
        rating: 5,
        comment:
          "Amazing experience! The scheme helped me buy my dream gold bracelet.",
      },
    ];

    // Return 4 random reviews per scheme
    const shuffled = allReviews.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 4);
  };

  const handleSchemeSelect = (schemeId) => {
    navigate(`/schemes-v2/simulation?scheme=${schemeId}`);
  };

  if (isLoading) {
    return (
      <Container maxWidth="xl" disableGutters>
        <Navbar />
        <Box
          sx={{
            background:
              "linear-gradient(to bottom, rgb(249, 236, 220), rgb(231, 192, 125))",
            minHeight: "100vh",
            pt: { xs: 15, md: 12 },
            pb: 4,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress size={60} sx={{ color: "#A36E29", mb: 2 }} />
          <Typography
            variant="h6"
            sx={{ color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}
          >
            Loading schemes...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" disableGutters>
        <Navbar />
        <Box
          sx={{
            background:
              "linear-gradient(to bottom, rgb(249, 236, 220), rgb(231, 192, 125))",
            minHeight: "100vh",
            pt: { xs: 15, md: 12 },
            pb: 4,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading schemes: {error}
          </Alert>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ backgroundColor: "#A36E29" }}
          >
            Retry
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        background:
          "linear-gradient(to bottom, rgb(249, 236, 220), rgb(231, 192, 125))",
        minHeight: "100vh",
        width: "100%",
        overflow: "visible",
      }}
    >
      <Navbar />

      <Container maxWidth="100vw" disableGutters sx={{ overflow: "visible" }}>
        {/* Scheme Images Section */}
        <Container maxWidth="xl" sx={{ mb: 6, px: 0 }}>
          {/* <Box sx={{ textAlign: "center", mb: 5 }}>
            <Typography
              variant="h3"
              sx={{
                background: "linear-gradient(135deg, #A36E29 0%, #d4a76a 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontWeight: 800,
                mb: 2,
                fontFamily: '"Roboto", sans-serif',
                fontSize: { xs: "2rem", md: "2.5rem" }
              }}
            >
              Our Schemes in Action
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "rgba(163, 110, 41, 0.7)",
                fontWeight: 400,
                fontFamily: '"Roboto", sans-serif'
              }}
            >
              See how our schemes work in real life
            </Typography>
          </Box> */}

          {/* Scheme Images Display */}
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "100%",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 0,
              mb: 6,
              p: 0,
            }}
          >
            {(() => {
              const schemeParam = searchParams.get("scheme");
              const selectedScheme = schemes.find(
                (scheme) => scheme.id === schemeParam
              );

              // Show specific image based on selected scheme
              if (selectedScheme) {
                let imageSrc = "/assets/23.webp"; // Default image

                // Map scheme IDs to specific images
                switch (selectedScheme.id) {
                  case "11-month-direct-purchase":
                    imageSrc = "/assets/24.webp";
                    break;
                  case "30-day-daily-savings":
                    imageSrc = "/assets/22.webp";
                    break;
                  case "10-month-accumulation":
                    imageSrc = "/assets/23.webp";
                    break;
                  default:
                    imageSrc = "/assets/23.webp";
                }

                return (
                  <Box
                    component="img"
                    src={imageSrc}
                    alt={`${selectedScheme.name} Scheme`}
                    sx={{
                      width: "100vw",
                      height: "auto",
                      borderRadius: "0px",
                      cursor: "pointer",
                      objectFit: "cover",
                      padding: "0px",
                      margin: "0px calc(50% - 50vw)",
                      display: "block",
                      marginTop: { xs: "10rem", md: "0px" },
                      "@media (min-width: 768px)": {
                        margin: "0px",
                      },
                    }}
                  />
                );
              } else {
                // Show all images when no specific scheme is selected
                return (
                  <>
                    <Box
                      component="img"
                      src="/assets/23.webp"
                      alt="Scheme 1"
                      sx={{
                        width: "100vw",
                        height: "auto",
                        borderRadius: "0px",
                        cursor: "pointer",
                        objectFit: "cover",
                        padding: "0px",
                        margin: "0px calc(50% - 50vw)",
                        display: "block",
                        marginTop: { xs: "10rem", md: "0px" },
                        "@media (min-width: 768px)": {
                          margin: "0px",
                        },
                      }}
                    />
                    <Box
                      component="img"
                      src="/assets/22.webp"
                      alt="Scheme 2"
                      sx={{
                        width: "100vw",
                        height: "auto",
                        borderRadius: "0px",
                        cursor: "pointer",
                        objectFit: "cover",
                        padding: "0px",
                        margin: "0px calc(50% - 50vw)",
                        display: "block",
                        marginTop: { xs: "10rem", md: "0px" },
                        "@media (min-width: 768px)": {
                          margin: "0px",
                        },
                      }}
                    />
                    <Box
                      component="img"
                      src="/assets/24.webp"
                      alt="Scheme 3"
                      sx={{
                        width: "100vw",
                        height: "auto",
                        borderRadius: "0px",
                        cursor: "pointer",
                        objectFit: "cover",
                        padding: "0px",
                        margin: "0px calc(50% - 50vw)",
                        display: "block",
                        marginTop: { xs: "10rem", md: "0px" },
                        "@media (min-width: 768px)": {
                          margin: "0px",
                        },
                      }}
                    />
                  </>
                );
              }
            })()}
          </Box>
        </Container>
        {/* Statistics Section */}
        <Container maxWidth="lg" sx={{ mb: 6 }}>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            {schemes
              .filter((scheme) => {
                const schemeParam = searchParams.get("scheme");
                return !schemeParam || scheme.id === schemeParam;
              })
              .map((scheme) => {
                const stats = getDummyStats(scheme.id);
                return (
                  <Grid
                    container
                    spacing={2}
                    key={`stats-${scheme.id}`}
                    justifyContent="center"
                  >
                    <Grid item xs={6} sm={4} md={2.4}>
                      <Box
                        sx={{
                          textAlign: "center",
                          p: 2,
                          backgroundColor: "rgba(163, 110, 41, 0.05)",
                          borderRadius: 2,
                          border: "1px solid rgba(163, 110, 41, 0.1)",
                        }}
                      >
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 800,
                            color: "#A36E29",
                            fontFamily: '"Roboto", sans-serif',
                            mb: 0.5,
                          }}
                        >
                          {stats.enrolled}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(163, 110, 41, 0.7)",
                            fontWeight: 500,
                            fontFamily: '"Roboto", sans-serif',
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Enrolled
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6} sm={4} md={2.4}>
                      <Box
                        sx={{
                          textAlign: "center",
                          p: 2,
                          backgroundColor: "rgba(163, 110, 41, 0.05)",
                          borderRadius: 2,
                          border: "1px solid rgba(163, 110, 41, 0.1)",
                        }}
                      >
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 800,
                            color: "#A36E29",
                            fontFamily: '"Roboto", sans-serif',
                            mb: 0.5,
                          }}
                        >
                          ₹{stats.invested}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(163, 110, 41, 0.7)",
                            fontWeight: 500,
                            fontFamily: '"Roboto", sans-serif',
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Total Invested
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6} sm={4} md={2.4}>
                      <Box
                        sx={{
                          textAlign: "center",
                          p: 2,
                          backgroundColor: "rgba(163, 110, 41, 0.05)",
                          borderRadius: 2,
                          border: "1px solid rgba(163, 110, 41, 0.1)",
                        }}
                      >
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 800,
                            color: "#A36E29",
                            fontFamily: '"Roboto", sans-serif',
                            mb: 0.5,
                          }}
                        >
                          {stats.avgMonthly || stats.avgDaily}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(163, 110, 41, 0.7)",
                            fontWeight: 500,
                            fontFamily: '"Roboto", sans-serif',
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Avg Amount
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6} sm={4} md={2.4}>
                      <Box
                        sx={{
                          textAlign: "center",
                          p: 2,
                          backgroundColor: "rgba(163, 110, 41, 0.05)",
                          borderRadius: 2,
                          border: "1px solid rgba(163, 110, 41, 0.1)",
                        }}
                      >
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 800,
                            color: "#A36E29",
                            fontFamily: '"Roboto", sans-serif',
                            mb: 0.5,
                          }}
                        >
                          {stats.completionRate}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(163, 110, 41, 0.7)",
                            fontWeight: 500,
                            fontFamily: '"Roboto", sans-serif',
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Completion Rate
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6} sm={4} md={2.4}>
                      <Box
                        sx={{
                          textAlign: "center",
                          p: 2,
                          backgroundColor: "rgba(163, 110, 41, 0.05)",
                          borderRadius: 2,
                          border: "1px solid rgba(163, 110, 41, 0.1)",
                        }}
                      >
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 800,
                            color: "#A36E29",
                            fontFamily: '"Roboto", sans-serif',
                            mb: 0.5,
                          }}
                        >
                          {stats.satisfaction}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(163, 110, 41, 0.7)",
                            fontWeight: 500,
                            fontFamily: '"Roboto", sans-serif',
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Satisfaction
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                );
              })}
          </Box>
        </Container>
        {/* Header Section */}
        <Container maxWidth="lg" sx={{ mb: 4 }}>
          {(() => {
            const schemeParam = searchParams.get("scheme");
            const selectedScheme = schemes.find(
              (scheme) => scheme.id === schemeParam
            );

            if (selectedScheme) {
              return (
                <>
                  <Typography
                    variant="h3"
                    sx={{
                      color: "#A36E29",
                      fontWeight: 700,
                      textAlign: "center",
                      mb: 2,
                      fontSize: { xs: "2rem", md: "3rem" },
                      fontFamily: '"Roboto", sans-serif',
                    }}
                  >
                    {selectedScheme.name}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "rgba(163, 110, 41, 0.8)",
                      textAlign: "center",
                      fontWeight: 400,
                      mb: 4,
                      fontFamily: '"Roboto", sans-serif',
                    }}
                  >
                    {getSchemeDescription(selectedScheme.id)}
                  </Typography>
                </>
              );
            }

            return (
              <>
                <Typography
                  variant="h3"
                  sx={{
                    color: "#A36E29",
                    fontWeight: 700,
                    textAlign: "center",
                    mb: 2,
                    fontSize: { xs: "2rem", md: "3rem" },
                    fontFamily: '"Roboto", sans-serif',
                  }}
                >
                  Choose Your Savings Plan
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: "rgba(163, 110, 41, 0.8)",
                    textAlign: "center",
                    fontWeight: 400,
                    mb: 4,
                    fontFamily: '"Roboto", sans-serif',
                  }}
                >
                  Select the perfect scheme that matches your savings goals and
                  timeline.
                </Typography>
              </>
            );
          })()}
        </Container>

        {/* Schemes Grid */}
        <Container maxWidth="lg" sx={{ mb: 4, py: 4, mt: 2 }}>
          <Grid container spacing={3} justifyContent="center">
            {schemes
              .filter((scheme) => {
                const schemeParam = searchParams.get("scheme");
                return !schemeParam || scheme.id === schemeParam;
              })
              .map((scheme) => (
                <Grid
                  item
                  xs={12}
                  md={4}
                  key={scheme.id}
                  sx={{
                    p: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >

                <Box
                sx={{
                  position: "relative",
                  display: "inline-block",
                }}
                >
                    {scheme.id === "11-month-direct-purchase" && (
                      <Box
                      component="img"
                      src={HighlyRecommended}
                      alt="Highly Recommended"
                      sx={{
                        position: "absolute",
                        top: "-25px",
                        left: "-100px",
                        width: "280px",
                        transform: "rotate(-35deg)",
                        zIndex: "10",
                        pointerEvents: "none",
                        height: "100px",
                      }}
                      >
                      </Box>
                    )}
                  <Box
                    sx={{
                      height: "100%",
                      width: "100%",
                      borderRadius: 4,
                      boxShadow: "0 12px 40px rgba(163, 110, 41, 0.15)",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      cursor: "pointer",
                      background:
                        "linear-gradient(145deg, #ffffff 0%, #fafafa 100%)",
                      border: "2px solid rgba(163, 110, 41, 0.1)",
                      overflow: "hidden",
                      position: "relative",
                      "&:hover": {
                        transform: "translateY(-8px) scale(1.02)",
                        boxShadow: "0 20px 60px rgba(163, 110, 41, 0.25)",
                        border: "2px solid rgba(163, 110, 41, 0.2)",
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background:
                          "linear-gradient(90deg, #A36E29 0%, #d4a76a 100%)",
                        opacity: 0,
                        transition: "opacity 0.3s ease",
                      },
                      "&:hover::before": {
                        opacity: 1,
                      },
                    }}
                    onClick={() => handleSchemeSelect(scheme.id)}
                  >
                    <Box
                      sx={{
                        p: 3,
                        height: "90%",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                      }}
                    >
                      {/* Scheme Icon */}
                      <Box sx={{ textAlign: "center", mb: 2 }}>
                        {getSchemeIcon(scheme.id)}
                      </Box>

                      {/* Scheme Name */}
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: "#A36E29",
                          textAlign: "center",
                          mb: 2,
                          fontFamily: '"Roboto", sans-serif',
                        }}
                      >
                        {scheme.name}
                      </Typography>

                      {/* Scheme Description */}
                      <Typography
                        variant="body1"
                        sx={{
                          color: "text.secondary",
                          textAlign: "center",
                          mb: 3,
                          fontFamily: '"Roboto", sans-serif',
                          flexGrow: 1,
                        }}
                      >
                        {getSchemeDescription(scheme.id)}
                      </Typography>

                      {/* Scheme Benefits */}
                      <Box
                        sx={{
                          mb: 3,
                          display: "flex",
                          justifyContent: "center",
                          flexWrap: "wrap",
                          gap: 1,
                        }}
                      >
                        {getSchemeBenefits(scheme.id).map((benefit, index) => (
                          <Chip
                            key={index}
                            label={benefit}
                            size="small"
                            sx={{
                              backgroundColor: "rgba(163, 110, 41, 0.1)",
                              color: "#A36E29",
                              fontWeight: 600,
                              fontFamily: '"Roboto", sans-serif',
                            }}
                          />
                        ))}
                      </Box>

                      {/* Scheme Details */}
                      <Box sx={{ mb: 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontFamily: '"Roboto", sans-serif' }}
                          >
                            {scheme.dailyInstalment
                              ? "Daily Amount"
                              : "Monthly Amount"}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontFamily: '"Roboto", sans-serif',
                            }}
                          >
                            ₹
                            {(
                              scheme.monthlyInstalment ||
                              scheme.dailyInstalment ||
                              0
                            ).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontFamily: '"Roboto", sans-serif' }}
                          >
                            Duration
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontFamily: '"Roboto", sans-serif',
                            }}
                          >
                            {scheme.tenure ||
                              (scheme.dailyInstalment
                                ? "30 days"
                                : "10-11 months")}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontFamily: '"Roboto", sans-serif' }}
                          >
                            Total Amount
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontFamily: '"Roboto", sans-serif',
                            }}
                          >
                            ₹
                            {scheme.totalAmount?.toLocaleString() || "Flexible"}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Action Button */}
                      <Button
                        variant="contained"
                        fullWidth
                        sx={{
                          background:
                            "linear-gradient(135deg, #A36E29 0%, #d4a76a 100%)",
                          py: 2,
                          fontWeight: 700,
                          fontFamily: '"Roboto", sans-serif',
                          borderRadius: 3,
                          textTransform: "none",
                          fontSize: "1.1rem",
                          boxShadow: "0 6px 20px rgba(163, 110, 41, 0.3)",
                          position: "relative",
                          overflow: "hidden",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #8b5a1f 0%, #c18a3d 100%)",
                            boxShadow: "0 8px 25px rgba(163, 110, 41, 0.4)",
                            transform: "translateY(-2px)",
                          },
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: "-100%",
                            width: "100%",
                            height: "100%",
                            background:
                              "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                            transition: "left 0.5s",
                          },
                          "&:hover::before": {
                            left: "100%",
                          },
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          mt: "auto",
                          mb: 1,
                        }}
                      >
                        View Details
                      </Button>
                    </Box>
                  </Box>
                  </Box>
                </Grid>
              ))}
          </Grid>
        </Container>

        {/* Customer Reviews Section */}
        <Container maxWidth="xl" sx={{ mb: 6 }}>
          <Box sx={{ textAlign: "center", mb: 5 }}>
            <Typography
              variant="h3"
              sx={{
                background: "linear-gradient(135deg, #A36E29 0%, #d4a76a 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontWeight: 800,
                mb: 2,
                fontFamily: '"Roboto", sans-serif',
                fontSize: { xs: "2rem", md: "2.5rem" },
              }}
            >
              What Our Customers Say
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "rgba(163, 110, 41, 0.7)",
                fontWeight: 400,
                fontFamily: '"Roboto", sans-serif',
              }}
            >
              Real experiences from satisfied customers
            </Typography>
          </Box>

          {/* Horizontal Reviews Carousel */}
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              borderRadius: 4,
              background:
                "linear-gradient(135deg, rgba(163, 110, 41, 0.05) 0%, rgba(212, 167, 106, 0.02) 100%)",
              p: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 3,
                overflowX: "auto",
                scrollBehavior: "smooth",
                pb: 0,
                "&::-webkit-scrollbar": {
                  height: 6,
                },
                "&::-webkit-scrollbar-track": {
                  background: "rgba(163, 110, 41, 0.1)",
                  borderRadius: 3,
                },
                "&::-webkit-scrollbar-thumb": {
                  background:
                    "linear-gradient(90deg, #A36E29 0%, #d4a76a 100%)",
                  borderRadius: 3,
                },
              }}
            >
              {schemes
                .filter((scheme) => {
                  const schemeParam = searchParams.get("scheme");
                  return !schemeParam || scheme.id === schemeParam;
                })
                .flatMap((scheme) =>
                  getDummyReviews(scheme.id).map((review, index) => ({
                    ...review,
                    schemeName: scheme.name,
                    schemeId: scheme.id,
                    reviewId: `${scheme.id}-${index}`,
                  }))
                )
                .map((review) => (
                  <Box
                    key={review.reviewId}
                    sx={{
                      minWidth: { xs: "280px", sm: "320px", md: "350px" },
                      maxWidth: { xs: "280px", sm: "320px", md: "350px" },
                      p: 4,
                      background:
                        "linear-gradient(145deg, #ffffff 0%, #fafafa 100%)",
                      borderRadius: 4,
                      boxShadow: "0 8px 32px rgba(163, 110, 41, 0.12)",
                      border: "2px solid rgba(163, 110, 41, 0.08)",
                      position: "relative",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 12px 40px rgba(163, 110, 41, 0.2)",
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "3px",
                        background:
                          "linear-gradient(90deg, #A36E29 0%, #d4a76a 100%)",
                        borderRadius: "4px 4px 0 0",
                      },
                    }}
                  >
                    {/* Scheme Badge */}
                    <Box
                      sx={{
                        display: "inline-block",
                        px: 2,
                        py: 0.5,
                        background:
                          "linear-gradient(135deg, #A36E29 0%, #d4a76a 100%)",
                        borderRadius: 2,
                        mb: 3,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "white",
                          fontWeight: 600,
                          fontFamily: '"Roboto", sans-serif',
                          fontSize: "0.75rem",
                        }}
                      >
                        {review.schemeName}
                      </Typography>
                    </Box>

                    {/* Stars */}
                    <Box sx={{ display: "flex", gap: 0.5, mb: 2 }}>
                      {[...Array(5)].map((_, i) =>
                        i < review.rating ? (
                          <StarIcon
                            key={i}
                            sx={{ fontSize: 20, color: "#FFD700" }}
                          />
                        ) : (
                          <StarBorderIcon
                            key={i}
                            sx={{ fontSize: 20, color: "#DDD" }}
                          />
                        )
                      )}
                    </Box>

                    {/* Review Text */}
                    <Typography
                      variant="body1"
                      sx={{
                        color: "text.secondary",
                        fontStyle: "italic",
                        fontFamily: '"Roboto", sans-serif',
                        lineHeight: 1.6,
                        mb: 3,
                        fontSize: "0.95rem",
                      }}
                    >
                      "{review.comment}"
                    </Typography>

                    {/* Customer Info */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        pt: 2,
                        borderTop: "1px solid rgba(163, 110, 41, 0.1)",
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          background:
                            "linear-gradient(135deg, #A36E29 0%, #d4a76a 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          fontWeight: 600,
                          fontSize: "1.1rem",
                          fontFamily: '"Roboto", sans-serif',
                        }}
                      >
                        {review.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </Box>
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            fontFamily: '"Roboto", sans-serif',
                            color: "#A36E29",
                          }}
                        >
                          {review.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontFamily: '"Roboto", sans-serif',
                          }}
                        >
                          Verified Customer
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
            </Box>
          </Box>

          {/* Scroll Indicator */}
          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(163, 110, 41, 0.6)",
                fontFamily: '"Roboto", sans-serif',
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#A36E29",
                }}
              />
              Scroll to see more reviews
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#A36E29",
                }}
              />
            </Typography>
          </Box>
        </Container>
      </Container>
    </Box>
  );
};

export default SchemesV2Selection;
